import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Cookies } from 'react-cookie';
import { Controller, type Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextareaAutosize,
  Typography,
  InputLabel,
} from '@mui/material';
import ErrorMessage from 'components/ErrorMessage';
import { InfoIcon } from 'components/Icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import type { UserSettings } from 'types/user';
import { useUserSettings } from 'selectors/user';
import { trackEvent } from '@surfline/web-common';
import { convertToSeconds, formatTimestampInTimezone } from 'utils/time';
import {
  useForecastFeedbackModalOpenState,
  useForecastFeedbackFormSubmissionSuccess,
} from 'utils/forecastFeedbackState';
import ThumbSelector from 'components/ThumbSelector';
import FeedbackModalWrapper from 'components/FeedbackModalWrapper/FeedbackModalWrapper';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';
import { getConditionItems, getWaveSizeItems, useDayItems } from '../utils/inputValues';
import { isWhenDayYesterdayInLocalCalendar } from '../utils/isWhenDayYesterdayInLocalCalendar';
import { createNoResponse, createYesResponse } from '../utils/requests';

import ForecastFeedbackFormSchema, { ForecastFeedbackWhenSchema } from '../types';

import styles from '../ForecastFeedbackForms.module.scss';

export interface Props {
  isAccurateFeedback: boolean;
  spotId: string;
  spotName: string;
  timezone: string;
  utcOffset: number;
}

type FormSchemaType = z.infer<typeof ForecastFeedbackFormSchema>;

const LoadingIcon: React.FC = () => (
  <Box className={styles.loadingIcon}>
    <CircularProgress
      size={20}
      sx={{ color: 'grey' }}
      data-testid="forecast-feedback-button-loading"
    />
  </Box>
);

const AddForm: React.FC<Props> = ({
  isAccurateFeedback,
  spotId,
  spotName,
  timezone,
  utcOffset = 0,
}) => {
  const userSettings: UserSettings | null = useUserSettings();
  const cookie = new Cookies();
  const accessToken = cookie.get('access_token');
  const [isSetTimeButtonDisabled, setIsSetTimeButtonDisabled] = useState(true);
  const surfHeightSetting = userSettings?.units.surfHeight || 'FT';
  const windSpeedSetting = userSettings?.units.windSpeed || 'MPH';
  const validationSchema = isAccurateFeedback
    ? ForecastFeedbackWhenSchema
    : ForecastFeedbackFormSchema;
  const {
    control,
    formState: { errors, isSubmitting, isValid },
    getValues,
    handleSubmit,
    register,
    setValue,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(validationSchema) as unknown as Resolver<FormSchemaType>,
  });
  const [formStep, setFormStep] = useState(0);
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);
  const [, setSubmissionSuccess] = useForecastFeedbackFormSubmissionSuccess();
  const [formSubmissionError, setFormSubmissionError] = useState<string | null>(null);
  const [formSubmissionErrorCount, setFormSubmissionErrorCount] = useState(0);
  const [, setIsModalOpen] = useForecastFeedbackModalOpenState();
  const getWaveHeightLabelFromValue = (waveHeight: string) => {
    const waveSizeItems = getWaveSizeItems(surfHeightSetting);
    return waveSizeItems.find((item) => item.value === waveHeight)?.label;
  };
  const addSubmit: SubmitHandler<FormSchemaType> = async (formData: FormSchemaType) => {
    // The date and time is already presented to the user to select in the spots timezone
    // so we don't need to do any timezone conversion here
    const hour = formData.whenTime.getHours().toString().padStart(2, '0');
    const minutes = formData.whenTime.getMinutes().toString().padStart(2, '0');
    const timestampFormatted = `${formData.whenDay} ${hour}:${minutes}`;

    try {
      if (isAccurateFeedback) {
        await createYesResponse(
          accessToken,
          spotId,
          surfHeightSetting,
          windSpeedSetting,
          timestampFormatted,
        );
        trackEvent('Forecast Feedback Submitted', {
          spotId,
          spotName,
          isAccurate: true,
          surfTime: timestampFormatted,
        });
      } else {
        await createNoResponse(
          accessToken,
          spotId,
          surfHeightSetting,
          formData.waveSize,
          formData.conditions,
          formData.wind,
          windSpeedSetting,
          formData.notes,
          timestampFormatted,
        );
        trackEvent('Forecast Feedback Submitted', {
          spotId,
          spotName,
          isAccurate: false,
          surfTime: timestampFormatted,
          waveHeight: getWaveHeightLabelFromValue(formData.waveSize),
          conditions: formData.conditions,
        });
      }
      setSubmissionSuccess({ status: true });
    } catch (error: unknown) {
      const genericError = 'Try again to submit your feedback.';
      reportErrorToNewRelic(
        error as Error,
        'AddForm',
        isAccurateFeedback
          ? { spotId, surfHeightSetting }
          : {
              spotId,
              surfHeightSetting,
              waveSize: formData.waveSize,
              wind: formData.wind,
              notes: formData.notes,
            },
      );
      setFormSubmissionErrorCount((prevCount) => prevCount + 1);

      const hasResponse = (
        err: unknown,
      ): err is { response: { status: number; data?: { message?: string } } } => {
        return typeof err === 'object' && err !== null && 'response' in err;
      };

      if (hasResponse(error) && error.response.status >= 400 && error.response.status < 500) {
        setFormSubmissionError(error.response.data?.message ?? genericError);
      } else {
        setFormSubmissionError(genericError);
      }
    }
  };
  const dayItems = useDayItems(utcOffset);
  const waveSizeItems = getWaveSizeItems(surfHeightSetting);
  const conditionItems = getConditionItems();
  const selectedDateTime = useRef('');
  const submitButtonDisabled = useMemo(
    () => !isValid || isSubmitting || formSubmissionErrorCount >= 3,
    [formSubmissionErrorCount, isSubmitting, isValid],
  );
  const defaultTime = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const handleSetTimeClick = useCallback(() => {
    const whenDay = getValues('whenDay');
    const whenTime = getValues('whenTime');
    if (whenDay && whenTime) {
      const dayParts = whenDay.split('-');
      const today = new Date();
      const selectedDate = new Date(
        Date.UTC(
          Number(dayParts[0]),
          Number(dayParts[1]) - 1,
          Number(dayParts[2]),
          whenTime.getHours(),
          whenTime.getMinutes(),
        ),
      );
      const selectedDateTimestamp = convertToSeconds(selectedDate.getTime());
      // Note: we'll use utcOffset of 0 when formatting because that's how we created the date object above.
      // The date/ time that the user selected is already presented as if it's in the spot timezone.
      const formattedDay = isWhenDayYesterdayInLocalCalendar(whenDay, today)
        ? 'Yesterday'
        : formatTimestampInTimezone(selectedDateTimestamp, 0, 'EEE do');
      const formattedTime = formatTimestampInTimezone(selectedDateTimestamp, 0, 'h:mm a');
      selectedDateTime.current = `${formattedDay} at ${formattedTime}`;
      setFormStep(1);
    } else {
      setDateTimeError('Please enter a valid date and time');
    }
  }, [getValues]);
  const handleEditClick = useCallback(() => {
    setFormStep(0);
  }, [setFormStep]);
  const handleCloseClick = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  return (
    <FeedbackModalWrapper
      title={spotName}
      subTitle="We'd love to hear from you"
      handleCloseClick={handleCloseClick}
    >
      <form noValidate onSubmit={handleSubmit(addSubmit)}>
        {formStep === 0 && (
          <>
            <Box className={styles.message}>
              <Box className={styles.infoIcon}>
                <InfoIcon />
              </Box>
              <Typography component="p" variant="footnote">
                {isAccurateFeedback
                  ? 'Tell us when you surfed so we can match your feedback to the forecast.'
                  : 'Help us understand where we’re getting it wrong. We’ll use your input to improve our forecasts.'}
              </Typography>
            </Box>
            <Box className={styles.whenContainer}>
              {errors?.whenDay && (
                <Typography
                  component="p"
                  variant="caption"
                  className={styles.errorMessage}
                  data-testid="when-day-error"
                >
                  {errors?.whenDay?.message}
                </Typography>
              )}
              <Typography variant="headline" component="p" className={styles.inputHeading}>
                When did you surf?
              </Typography>
              <FormControl
                className={classNames(styles.inputsWrapper, errors?.whenDay ? styles.error : '')}
              >
                <InputLabel>Date</InputLabel>
                <Select
                  label="Date"
                  {...register('whenDay')}
                  data-testid="when-day-input"
                  className={styles.whenDayInput}
                  defaultValue={getValues('whenDay') || dayItems[0].value}
                >
                  {dayItems.map((item) => (
                    <MenuItem value={item.value} key={item.label}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors?.whenTime && (
                <Typography
                  component="p"
                  variant="caption"
                  className={styles.errorMessage}
                  data-testid="when-time-error"
                >
                  {errors?.whenTime?.message}
                </Typography>
              )}
              <FormControl
                className={classNames(styles.inputsWrapper, errors?.whenTime ? styles.error : '')}
                data-testid="when-time-input"
              >
                <Controller
                  name="whenTime"
                  control={control}
                  defaultValue={defaultTime}
                  render={() => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopTimePicker
                        label="Time"
                        onChange={(date: Date | null) => {
                          if (date) {
                            setValue('whenTime', date);
                            setIsSetTimeButtonDisabled(false);
                          }
                        }}
                        value={getValues('whenTime')}
                        ampm={false}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
              {dateTimeError && (
                <Typography
                  className={styles.errorMessage}
                  component="p"
                  data-testid="date-time-error"
                  variant="caption"
                >
                  {dateTimeError}
                </Typography>
              )}
              <Box className={styles.buttonsContainer}>
                {isAccurateFeedback ? (
                  <>
                    {formSubmissionError && (
                      <Box
                        className={styles.errorStateWrapper}
                        data-testid="forecast-feedback-error-wrapper"
                      >
                        <ErrorMessage
                          className={styles.errorState}
                          message="Something went wrong here"
                          subMessage={formSubmissionError}
                        />
                      </Box>
                    )}
                    <Button
                      className={styles.submitButton}
                      data-testid="add-button"
                      disabled={isSetTimeButtonDisabled || submitButtonDisabled}
                      disableFocusRipple
                      disableRipple
                      disableTouchRipple
                      type="submit"
                      variant="primary"
                    >
                      {isSubmitting && (
                        <span className={styles.loadingIcon}>
                          <LoadingIcon />
                        </span>
                      )}
                      Submit observation
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="set-time-button"
                    disabled={isSetTimeButtonDisabled}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    variant="secondary"
                    onClick={handleSetTimeClick}
                  >
                    Set time
                  </Button>
                )}
              </Box>
            </Box>
            <Typography
              variant="footnote"
              component="p"
              className={styles.timezone}
              data-testid="spot-timezone"
            >
              Time is in {timezone} time
            </Typography>
          </>
        )}
        {/* Accurate (Yes) flow never leaves step 0; step 1 is only for the No follow-up form. */}
        {formStep === 1 && !isAccurateFeedback && (
          <>
            {formSubmissionError ? (
              <Box
                className={styles.errorStateWrapper}
                data-testid="forecast-feedback-error-wrapper"
              >
                <ErrorMessage
                  className={styles.errorState}
                  message="Something went wrong here"
                  subMessage={formSubmissionError}
                />
              </Box>
            ) : (
              <>
                <Grid container className={styles.whenMessage}>
                  <Grid item mobile={11}>
                    <Typography component="p" variant="headline" className={styles.heading}>
                      When did you surf?
                    </Typography>
                    <Typography component="p" variant="body" data-testid="selected-date-time">
                      {selectedDateTime.current}
                    </Typography>
                  </Grid>
                  <Grid item mobile={1}>
                    <Typography
                      component="p"
                      variant="callout1"
                      className={styles.editLink}
                      onClick={handleEditClick}
                    >
                      Edit
                    </Typography>
                  </Grid>
                </Grid>
                {errors?.waveSize && (
                  <Typography
                    component="p"
                    variant="caption"
                    className={styles.errorMessage}
                    data-testid="wave-size-error"
                  >
                    {errors?.waveSize?.message}
                  </Typography>
                )}
                <Typography variant="subHeadline" component="p" className={styles.inputHeading}>
                  How big were the waves?
                </Typography>
                <FormControl
                  className={classNames(styles.inputsWrapper, errors?.waveSize ? styles.error : '')}
                >
                  <InputLabel className={styles.inputLabel}>
                    Wave height ({surfHeightSetting})
                  </InputLabel>
                  <Select
                    label={`Wave height (${surfHeightSetting})`}
                    {...register('waveSize')}
                    data-testid="wave-size-input"
                    className={styles.waveSizeInput}
                    defaultValue={getValues('waveSize') || ''}
                  >
                    {waveSizeItems.map((item) => (
                      <MenuItem value={item.value} key={item.label}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors?.conditions && (
                  <Typography
                    component="p"
                    variant="caption"
                    className={styles.errorMessage}
                    data-testid="conditions-error"
                  >
                    {errors?.conditions?.message}
                  </Typography>
                )}
                <Typography variant="subHeadline" component="p" className={styles.inputHeading}>
                  How was the surf?
                </Typography>
                <FormControl
                  className={classNames(
                    styles.inputsWrapper,
                    errors?.conditions ? styles.error : '',
                  )}
                >
                  <InputLabel className={styles.inputLabel}>Conditions</InputLabel>
                  <Select
                    label="Conditions"
                    {...register('conditions')}
                    data-testid="conditions-input"
                    className={styles.conditionsInput}
                    defaultValue={getValues('conditions') || ''}
                  >
                    {conditionItems.map((item) => (
                      <MenuItem value={item.value} key={item.label}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors?.wind && (
                  <Typography
                    component="p"
                    variant="caption"
                    className={styles.errorMessage}
                    data-testid="wind-error"
                  >
                    {errors?.wind?.message}
                  </Typography>
                )}
                <Typography variant="subHeadline" component="p" className={styles.inputHeading}>
                  Was the wind accurate?
                </Typography>
                <FormControl
                  className={classNames(styles.inputsWrapper, errors?.wind ? styles.error : '')}
                >
                  <Controller
                    name="wind"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ThumbSelector
                        onChange={onChange}
                        thumbsUp={value ? value === 'yes' : undefined}
                      />
                    )}
                  />
                </FormControl>
                {errors?.notes && (
                  <Typography
                    component="p"
                    variant="caption"
                    className={styles.errorMessage}
                    data-testid="notes-error"
                  >
                    {errors?.notes?.message}
                  </Typography>
                )}
                <Typography variant="subHeadline" component="p" className={styles.inputHeading}>
                  Anything you&apos;d like to add?
                </Typography>
                <FormControl
                  className={classNames(styles.inputsWrapper, errors?.notes ? styles.error : '')}
                >
                  <TextareaAutosize
                    {...register('notes')}
                    className={styles.notesInput}
                    data-testid="notes-input"
                    defaultValue={getValues('notes')}
                    maxLength={280}
                    minRows={5}
                    placeholder="Optional notes"
                  />
                </FormControl>
              </>
            )}
            <Box className={styles.buttonsContainer}>
              <Button
                className={styles.submitButton}
                data-testid="add-button"
                disabled={submitButtonDisabled}
                disableFocusRipple
                disableRipple
                disableTouchRipple
                type="submit"
                variant="primary"
              >
                {isSubmitting && (
                  <span className={styles.loadingIcon}>
                    <LoadingIcon />
                  </span>
                )}
                Submit observation
              </Button>
            </Box>
          </>
        )}
      </form>
    </FeedbackModalWrapper>
  );
};

export default AddForm;
